import type { ReactNode } from 'react';
import type {
  ChatUiMobilityPlannerPlace,
  MobilityPlannerBlock,
  MobilityPlannerTripType,
} from '@voll-intelligence/genui-contract';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { columnWidth, LABEL, recordPlaceText, ROW, StaticPill } from './planner_parts.js';
import { whenText } from './planner_when.js';

type Place = ChatUiMobilityPlannerPlace;

/** As respostas que o card travado mostra: as do card vivo, que seguem no
 *  registro quando a conversa anda. */
export interface PlannerAnswers {
  origin: Place | undefined;
  destination: Place | undefined;
  tripType: MobilityPlannerTripType;
  passengers: number;
  backOrigin: Place | undefined;
  backDestination: Place | undefined;
  /** O Quando editável (`YYYY-MM-DDTHH:mm`, ou o pedaço já entendido). */
  when: string;
}

/** Uma coluna do REGISTRO: a pergunta e a resposta como termo e definição, a
 *  resposta na pílula `sand-3` — só leitura, nenhum controle. `srOnly` esconde
 *  a pergunta da vista (a linha da Volta não repete os rótulos). */
function RecordColumn({
  label,
  wide,
  srOnly = false,
  children,
}: Readonly<{
  label: string;
  wide: boolean | 'auto';
  srOnly?: boolean;
  children: ReactNode;
}>): ReactNode {
  const width = wide === 'auto' ? 'min-w-0 flex-none' : columnWidth(wide);
  return (
    <div className={`grid gap-1 ${width}`}>
      <dt className={srOnly ? 'sr-only' : LABEL}>{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </div>
  );
}

/**
 * O card TRAVADO (turno superado ou conversa encerrada) vira registro: as
 * respostas, sem nenhum controle — nada recebe foco nem Tab, e o leitor de tela
 * lê pares de pergunta e resposta. Com o Sentido, a linha do Quem/Sentido/
 * Pessoas em cima e, na ida e volta, a Volta em linha própria.
 */
export function PlannerRecord({
  ui,
  answers,
  tripMode,
  emptyText,
}: Readonly<{
  ui: MobilityPlannerBlock;
  answers: PlannerAnswers;
  tripMode: boolean;
  emptyText: string;
}>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const roundTrip = tripMode && answers.tripType === 'round_trip';
  const pending = t('mobilityPlanner.whenPending');
  const back = t('mobilityPlanner.return');
  return (
    <div className="grid gap-3">
      {tripMode && (
        <dl className="m-0 flex flex-wrap items-end gap-2">
          {ui.who && (
            <RecordColumn label={t('mobilityPlanner.who')} wide="auto">
              <StaticPill icon="who" text={ui.who} />
            </RecordColumn>
          )}
          <RecordColumn label={t('mobilityPlanner.tripType')} wide="auto">
            <StaticPill
              text={t(
                answers.tripType === 'round_trip'
                  ? 'mobilityPlanner.tripType.roundTrip'
                  : 'mobilityPlanner.tripType.oneWay',
              )}
            />
          </RecordColumn>
          <RecordColumn label={t('mobilityPlanner.passengers')} wide="auto">
            <StaticPill
              text={t(
                answers.passengers === 1
                  ? 'mobilityPlanner.passengersCount_one'
                  : 'mobilityPlanner.passengersCount_other',
                { count: answers.passengers },
              )}
            />
          </RecordColumn>
        </dl>
      )}
      <div className="grid gap-1">
        {roundTrip && <p className={`m-0 ${LABEL}`}>{t('mobilityPlanner.outbound')}</p>}
        <dl className={`m-0 ${ROW}`}>
          <RecordColumn label={t('mobilityPlanner.origin')} wide>
            <StaticPill
              icon="place"
              text={recordPlaceText(answers.origin, ui.origin)}
              emptyText={emptyText}
            />
          </RecordColumn>
          {ui.destination && (
            <RecordColumn label={t('mobilityPlanner.destination')} wide>
              <StaticPill
                icon="place"
                text={recordPlaceText(answers.destination, ui.destination)}
                emptyText={emptyText}
              />
            </RecordColumn>
          )}
          {(ui.when !== undefined || tripMode || ui.editableWhen !== undefined) && (
            <RecordColumn label={t('mobilityPlanner.when')} wide={false}>
              <StaticPill
                icon="when"
                text={whenText(ui, answers.when, locale)}
                emptyText={pending}
              />
            </RecordColumn>
          )}
        </dl>
      </div>
      {roundTrip && (
        <div className="grid gap-1">
          <p className={`m-0 ${LABEL}`}>{back}</p>
          <dl className={`m-0 ${ROW}`}>
            <RecordColumn label={`${back} · ${t('mobilityPlanner.origin')}`} wide srOnly>
              <StaticPill
                icon="place"
                text={recordPlaceText(answers.backOrigin, ui.return?.origin)}
                emptyText={emptyText}
              />
            </RecordColumn>
            <RecordColumn label={`${back} · ${t('mobilityPlanner.destination')}`} wide srOnly>
              <StaticPill
                icon="place"
                text={recordPlaceText(answers.backDestination, ui.return?.destination)}
                emptyText={emptyText}
              />
            </RecordColumn>
            <RecordColumn label={`${back} · ${t('mobilityPlanner.when')}`} wide={false} srOnly>
              <StaticPill icon="when" text={ui.return?.when} emptyText={pending} />
            </RecordColumn>
          </dl>
        </div>
      )}
    </div>
  );
}
